/**
 * audioReceiverStats — the single inbound-RTP audio stats reader.
 *
 * Factored out of BroadcastMonitor so there is exactly one place that reads
 * `receiver.getStats()` → filters `inbound-rtp` → builds a typed snapshot.
 * BroadcastMonitor uses the packet counters for the server-broadcast
 * participant; the dead-air detector's flow checks use the byte/energy/
 * concealment fields for a single named speaker.
 *
 * Every field beyond the three spec-mandatory counters is optional: some
 * browsers don't expose `totalAudioEnergy` / `concealedSamples`, so the flow
 * checks must degrade — never false-fire — when a field is `undefined`. The
 * remaining fields are captured for diagnostics only and are never gated on.
 */

export interface AudioReceiverStats {
  /** Spec-mandatory everywhere. */
  packetsReceived: number
  /** Spec-mandatory everywhere. */
  packetsLost: number
  /** Spec-mandatory everywhere; drives `delivery_stalled`. */
  bytesReceived: number
  /** Some browsers don't expose this; drives `silent_decode` detection. */
  totalAudioEnergy?: number
  /** Ships with energy; normalizes the energy delta. */
  totalSamplesDuration?: number
  /** Some browsers don't expose this; the masked-packet-loss exclusion. */
  concealedSamples?: number
  /** Ships with concealedSamples. */
  concealmentEvents?: number
  /**
   * Total samples the decoder has produced. The denominator concealedSamples
   * is measured against: the concealed fraction is only meaningful per sample
   * delivered, and taking it from the receiver means no sample rate has to be
   * assumed.
   */
  totalSamplesReceived?: number

  // Diagnostic-only fields (captured, never gated on): jitter buffer health,
  // packet timing, and the concealment breakdown that explains flat energy.
  packetsDiscarded?: number
  jitter?: number
  jitterBufferDelay?: number
  jitterBufferEmittedCount?: number
  silentConcealedSamples?: number
  insertedSamplesForDeceleration?: number
  removedSamplesForAcceleration?: number
  lastPacketReceivedTimestamp?: number
  codecId?: string

  /**
   * How many `inbound-rtp` entries the receiver's report held. Not a spec
   * field: the reader keeps the first entry, and this says whether there was
   * anything else it could have kept.
   *
   * Expected to be 1. Anything else means the snapshot describes one of
   * several accountings of the same audio, so the flow checks reading it are
   * working from a partial picture — which is worth seeing in the report
   * rather than guessing about after the fact.
   */
  inboundRtpEntryCount?: number
}

/**
 * Read a counter the spec says is always there. Falls back to `0` rather than
 * `undefined` so the required fields stay required.
 *
 * Parses with `parseFloat`, not `parseInt`: these counters are 64-bit and a
 * string form can arrive in exponential notation, where `parseInt('1.2e10', 10)`
 * would read `1` and quietly flatten a byte delta to nothing. It also keeps
 * this in step with {@link optionalNumber} below.
 */
function ensureNumber(data: unknown): number {
  if (typeof data === 'number') return data
  if (typeof data === 'string') {
    const n = parseFloat(data)
    return Number.isNaN(n) ? 0 : n
  }
  return 0
}

/**
 * Read a value that may legitimately be absent. Returns `undefined` — distinct
 * from `0` — so the caller can degrade instead of treating a missing field as
 * "zero energy" and false-firing.
 */
function optionalNumber(data: unknown): number | undefined {
  if (typeof data === 'number') return data
  if (typeof data === 'string') {
    const n = parseFloat(data)
    return Number.isNaN(n) ? undefined : n
  }
  return undefined
}

/** Read a string field that may be absent; anything non-string is `undefined`. */
function optionalString(data: unknown): string | undefined {
  return typeof data === 'string' ? data : undefined
}

/**
 * The minimal receiver surface we read: a track whose receiver hands back
 * something that can be walked with `forEach`, which is all `RTCStatsReport`
 * guarantees across browsers. Narrow enough that a test can pass a plain
 * object, wide enough that a LiveKit `RemoteAudioTrack` satisfies it.
 */
export interface StatsReadableTrack {
  receiver?: {
    getStats(): Promise<StatsReport>
  }
}

/** The walkable shape of an `RTCStatsReport`. */
export interface StatsReport {
  forEach(callback: (value: Record<string, unknown>) => void): void
}

/**
 * Read the inbound-RTP audio stats for one track. Returns `undefined` when the
 * receiver has no stats yet (track still resolving).
 */
export async function unpackAudioReceiverStats(
  track: StatsReadableTrack
): Promise<AudioReceiverStats | undefined> {
  const stats = await track.receiver?.getStats()
  if (!stats) return
  let receiverStats: AudioReceiverStats | undefined
  let inboundRtpEntryCount = 0
  stats.forEach((v) => {
    if (v.type === 'inbound-rtp') {
      inboundRtpEntryCount++
      // Keep the first entry rather than the last. `getStats()` here is
      // receiver-scoped, so every inbound-rtp in this report belongs to this
      // one audio receiver: a second entry would be another accounting of the
      // same audio, and picking either one is a guess. The count below ships
      // the guess with the snapshot instead of hiding it.
      if (receiverStats) return
      receiverStats = {
        packetsReceived: ensureNumber(v.packetsReceived),
        packetsLost: ensureNumber(v.packetsLost),
        bytesReceived: ensureNumber(v.bytesReceived),
        totalAudioEnergy: optionalNumber(v.totalAudioEnergy),
        totalSamplesDuration: optionalNumber(v.totalSamplesDuration),
        concealedSamples: optionalNumber(v.concealedSamples),
        concealmentEvents: optionalNumber(v.concealmentEvents),
        totalSamplesReceived: optionalNumber(v.totalSamplesReceived),
        packetsDiscarded: optionalNumber(v.packetsDiscarded),
        jitter: optionalNumber(v.jitter),
        jitterBufferDelay: optionalNumber(v.jitterBufferDelay),
        jitterBufferEmittedCount: optionalNumber(v.jitterBufferEmittedCount),
        silentConcealedSamples: optionalNumber(v.silentConcealedSamples),
        insertedSamplesForDeceleration: optionalNumber(
          v.insertedSamplesForDeceleration
        ),
        removedSamplesForAcceleration: optionalNumber(
          v.removedSamplesForAcceleration
        ),
        lastPacketReceivedTimestamp: optionalNumber(
          v.lastPacketReceivedTimestamp
        ),
        codecId: optionalString(v.codecId),
      }
    }
  })
  if (!receiverStats) return
  return { ...receiverStats, inboundRtpEntryCount }
}
